import { useState } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { submitIntake, getConfig } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { track } from "@/lib/analytics";
import { Check } from "lucide-react";

const DEFAULT_INTRO = "Three questions. This scopes the Sprint before we meet. Direct and specific is best.";
const DEFAULT_FIELDS = [
  { key: "stack", label: "Your stack", help: "What you run on. Languages, platforms, tools, infrastructure." },
  { key: "stage", label: "Your stage", help: "Where the business and the product are right now." },
  { key: "gap", label: "The gap", help: "The decision or problem you cannot resolve alone." },
];

const ENTERPRISE_FIELDS = [
  { key: "platforms", label: "Your platforms and infrastructure", help: "What you run on today. Core systems, cloud, data platforms, and the vendors in play." },
  { key: "ai_initiatives", label: "Your AI initiatives", help: "Where AI is live, piloted, or under pressure to go, and who is driving it." },
  { key: "frameworks", label: "Governance and compliance frameworks", help: "The frameworks your board and regulators expect. SOC 2, ISO, NIST, DORA, or sector specific." },
  { key: "exposure", label: "Board and regulatory exposure", help: "The scrutiny you are under and the decision that has to hold up under it." },
  { key: "success", label: "What a successful readout looks like", help: "The one outcome that would make this Sprint worth it for your leadership." },
];

const CONFIGS = {
  "founder-tech-clarity-sprint": { kicker: "Founder Tech Clarity Sprint", intro: DEFAULT_INTRO, fields: DEFAULT_FIELDS },
  "nonprofit-tech-ai-readiness-sprint": { kicker: "Nonprofit Tech and AI Readiness Sprint", intro: DEFAULT_INTRO, fields: DEFAULT_FIELDS },
  "concept-to-customer-clarity-sprint": { kicker: "Concept-to-Customer Clarity Sprint", intro: DEFAULT_INTRO, fields: DEFAULT_FIELDS },
  "enterprise-ai-readiness-governance-sprint": {
    kicker: "Enterprise AI Readiness and Governance Sprint",
    intro: "Five questions. This scopes the Sprint and frames the readout for your board and your regulators. Direct and specific is best.",
    fields: ENTERPRISE_FIELDS,
  },
};

const fieldClass =
  "w-full rounded-[2px] border border-[color:var(--rule)] bg-transparent px-4 py-3 exec-serif text-[16px] text-[color:var(--ink)] placeholder:text-[color:var(--grey)] focus:outline-none focus:border-[color:var(--copper)] transition-colors";

export default function Intake() {
  const [params] = useSearchParams();
  const { offer: offerParam } = useParams();
  const navigate = useNavigate();
  const offer = offerParam || "founder-tech-clarity-sprint";
  const config = CONFIGS[offer] || CONFIGS["founder-tech-clarity-sprint"];

  const email = params.get("email") || "";
  const sessionId = params.get("session_id") || "";
  const { data: appConfig = {} } = useQuery({ queryKey: ["config"], queryFn: getConfig });

  const [form, setForm] = useState({ email });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [booked, setBooked] = useState(false);

  const update = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email?.trim() || config.fields.some((f) => !(form[f.key] || "").trim())) {
      setError("Please complete all fields.");
      return;
    }
    setStatus("submitting");
    try {
      const responses = config.fields.map((f) => ({ question: f.label, answer: form[f.key] || "" }));
      await submitIntake({
        email: form.email,
        offer,
        session_id: sessionId,
        responses,
        stack: form.stack || "",
        stage: form.stage || "",
        gap: form.gap || "",
      });
      track("intake_submitted", { offer });
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  const bookReadout = () => {
    track("booking_start", { offer, from: "intake" });
    if (appConfig.motion_sprint_url) window.open(appConfig.motion_sprint_url, "_blank", "noopener");
  };

  return (
    <div className="exec-scope min-h-screen" data-testid="intake-page">
      <Seo title="Project intake — Copper & Vine" description="Copper & Vine project intake." noindex />
      <div className="exec-container pt-24 md:pt-28 pb-24 max-w-2xl">
        <p className="exec-eyebrow">{config.kicker}</p>
        <h1 className="font-extralight text-[clamp(30px,4vw,46px)] leading-[1.1] mt-5">Your intake</h1>
        <p className="exec-serif text-[17px] leading-[1.55] text-[color:var(--lead-ink)] mt-5">{config.intro}</p>

        {status === "success" ? (
          <div className="mt-10" data-testid="intake-success">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-full border border-[color:var(--copper)] flex items-center justify-center">
                <Check size={18} className="text-[color:var(--copper-deep)]" />
              </span>
              <h2 className="text-[12px] uppercase tracking-[0.18em] font-normal text-[color:var(--ink)]">Intake received</h2>
            </div>
            <p className="exec-serif text-[17px] leading-[1.55] text-[color:var(--lead-ink)] mt-5">
              That is everything we need to prepare. Book your readout and we will be ready.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-7">
              <button data-testid="intake-book-readout-button" className="exec-btn exec-btn-primary" onClick={bookReadout}>
                Book the readout
              </button>
              {!booked && (
                <button
                  data-testid="intake-booking-complete-button"
                  className="exec-btn exec-btn-secondary"
                  onClick={() => {
                    track("booking_complete", { offer });
                    setBooked(true);
                  }}
                >
                  I have booked my readout
                </button>
              )}
              {booked && (
                <span className="exec-serif text-[16px] text-[color:var(--copper-deep)] self-center">
                  Confirmed. See you at the readout.
                </span>
              )}
            </div>
          </div>
        ) : (
          <form data-testid="intake-form" onSubmit={submit} className="mt-10 space-y-6">
            <Field label="Email">
              <input
                data-testid="intake-email-input"
                type="email"
                value={form.email || ""}
                onChange={(e) => update("email", e.target.value)}
                className={fieldClass}
              />
            </Field>
            {config.fields.map((f) => (
              <Field key={f.key} label={f.label} help={f.help}>
                <textarea
                  data-testid={`intake-${f.key}-input`}
                  rows={3}
                  value={form[f.key] || ""}
                  onChange={(e) => update(f.key, e.target.value)}
                  className={`${fieldClass} resize-none`}
                />
              </Field>
            ))}
            {error && (
              <p
                data-testid="intake-form-error-message"
                className="exec-serif text-[16px] text-[color:var(--ink)] border-l-2 border-[color:var(--copper)] pl-3"
              >
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="intake-form-submit-button"
              disabled={status === "submitting"}
              className="exec-btn exec-btn-primary disabled:opacity-60"
            >
              {status === "submitting" ? "Sending\u2026" : "Submit intake"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, help, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.14em] font-normal text-[color:var(--ink)]">{label}</span>
      {help && <span className="block exec-serif text-[15px] text-[color:var(--grey)] mt-1">{help}</span>}
      <div className="mt-2">{children}</div>
    </label>
  );
}
